// @file Surface posts store
import { isAppUsing } from '@@/bits/flip'
import { getVuexStore } from '@@/bits/pinia'
import { getPostsByGroupByProperty } from '@@/bits/surface_posts_grouping_helper'
import { unboundedWatch, unboundedWatchEffect } from '@@/bits/vue'
import { GroupByTypes } from '@padlet/arvo'
import { postFilterResults } from 'native_bridge/actions'
import postMessage from 'native_bridge/post_message'
import { defineStore, storeToRefs } from 'pinia'
import { useSurfaceCurrentUserStore } from 'pinia/surface_current_user'
import { filterPosts, useSurfaceFilterStore } from 'pinia/surface_filter'
import { useSurfacePermissionsStore } from 'pinia/surface_permissions'
import { useSurfacePostSearchStore } from 'pinia/surface_post_search'
import { useSurfaceWishArrangementStore } from 'pinia/surface_wish_arrangement'
import type { Cid, Post, PostAttributes } from 'types'
import { computed, ComputedRef, Ref, ref } from 'vue'
import { RootState } from 'vuexstore/surface/types'

export const useSurfacePostsStore = defineStore('surfacePosts', () => {
  const surfaceVuexStore = getVuexStore<RootState>()
  const { currentUser } = storeToRefs(useSurfaceCurrentUserStore())
  const { sortedGroups, wishGroupBy, isSectionedAndNonmanualSorted, isNotSectionedAndNonmanualSorted } = storeToRefs(
    useSurfaceWishArrangementStore(),
  )
  const { hasSelectedFilters } = storeToRefs(useSurfaceFilterStore())
  const { canIModerate, canIWrite } = storeToRefs(useSurfacePermissionsStore())

  const isAPostDragging = ref(false)
  const isAPostDraggingAndNonmanualSorted = computed(() => {
    return isAPostDragging.value && isSectionedAndNonmanualSorted.value
  })
  const shouldAlwaysDisableDrag = computed(
    () => searchTerm.value !== '' || hasSelectedFilters.value || isNotSectionedAndNonmanualSorted.value,
  )
  const postCidBeingDragged = ref<string | undefined>(undefined)
  const postBeingDragged = computed(() => {
    if (postCidBeingDragged.value == null) return undefined
    return postEntitiesByCid.value[postCidBeingDragged.value]
  })

  // have this permission here instead of surface_permissions to avoid circular dependency between surface_posts and surface_permissions
  // so we avoid having to import postBeingDragged to surface_permissions
  const canIDragPosts = computed(() => {
    // drag always not allowed if these conditions are true
    if (shouldAlwaysDisableDrag.value) {
      return false
    }

    const wasWrittenByCurrentUser =
      currentUser.value?.id != null && postBeingDragged?.value?.author_id === currentUser.value?.id
    // check if user has permission to drag the post
    if (canIModerate.value || (wasWrittenByCurrentUser && canIWrite.value)) {
      // if user has permission to drag post we only allow dragging posts when they're grouped by certain properties
      return [GroupByTypes.None, GroupByTypes.Section].includes(wishGroupBy.value)
    }

    return false
  })

  // Sync isAPostDragging with Vuex
  void unboundedWatch(isAPostDragging, (isDragging) => {
    void surfaceVuexStore?.dispatch('changeDragging', { isAPostDragging: isDragging })
  })

  const allPostsCount = computed<number>(() => surfaceVuexStore?.getters['post/allPostsCount'] ?? 0)
  const publishedPostsCount = computed<number>(() => surfaceVuexStore?.getters['post/publishedPostsCount'] ?? 0)
  const submittedPostsCount = computed<number>(() => surfaceVuexStore?.getters['post/submittedPostsCount'] ?? 0)

  const vuexCurrentSortedPosts = computed(() => surfaceVuexStore?.getters['post/currentSortedPosts'] as Post[]) ?? []
  const currentSortedPosts = computed(() =>
    isAppUsing('surfaceSearchPosts') ? postsFilteredBySearchAndFilters.value : vuexCurrentSortedPosts.value,
  )
  const currentSortedPostsCids = computed(() => currentSortedPosts.value.map((p) => p.cid) ?? [])
  const currentPostsCount = computed(() => currentSortedPosts.value.length)

  const newestPostCid = computed(() => surfaceVuexStore?.getters['post/newestPostCid'] ?? [])
  const postsBySectionId = computed<{ [sectionId: number]: Post[] }>(
    () => surfaceVuexStore?.getters['post/postsBySectionId'] ?? {},
  )
  const sortedPostCidsBySectionId = computed<{ [sectionId: number]: Cid[] | undefined }>(
    () => surfaceVuexStore?.getters['post/sortedPostCidsBySectionId'] ?? {},
  )
  const postEntitiesByCid = computed<{ [postCid: string]: Post }>(
    () => surfaceVuexStore?.getters['post/postEntitiesByCid'] ?? {},
  )
  const postEntities = computed<Post[]>(() => Object.values(postEntitiesByCid.value))
  const sortedPostCids = computed<string[]>(() => surfaceVuexStore?.getters['post/sortedPostCids'] ?? {})
  const postCids = computed<string[]>(() => surfaceVuexStore?.getters['post/postCids'] ?? [])

  const hoveredGroupIdWhenDraggingPost = ref<string | number | undefined>(undefined)

  const allSortedPostsByGroup: ComputedRef<{
    [x: string]: Post[] | undefined
    [x: number]: Post[] | undefined
  }> = computed(() => {
    const sortedGroupIds = sortedGroups.value.map((group) => group.id)
    return getPostsByGroupByProperty(vuexCurrentSortedPosts.value, sortedGroupIds, wishGroupBy.value)
  })

  // Map of group ID to posts. Order of groups not guaranteed.
  const postsByGroup: ComputedRef<{
    [x: string]: Post[] | undefined
    [x: number]: Post[] | undefined
  }> = computed(() => {
    const sortedGroupIds = sortedGroups.value.map((group) => group.id)
    return getPostsByGroupByProperty(currentSortedPosts.value, sortedGroupIds, wishGroupBy.value)
  })
  // Posts by groups, but in order by which groups are sorted.
  const postsBySortedGroups: ComputedRef<Post[][]> = computed(() => {
    return sortedGroups.value.map((group) => postsByGroup.value[group.id]).filter((group) => group != null) as Post[][]
  })

  const hasFetchedPosts = computed(() => surfaceVuexStore?.getters['post/isInitialPaginatedDataLoadDone'])

  function startNewPost({
    attributes,
    wishOrder,
  }: {
    attributes?: PostAttributes
    wishOrder?: { previousCid: Cid; nextCid: Cid }
  }): void {
    void surfaceVuexStore?.dispatch('post/startNewPost', { attributes, wishOrder })
  }

  function sortPost(payload: {
    postCid: Cid
    previousCid?: Cid | null
    nextCid?: Cid | null
    sortIndex?: number | null
    sectionId?: number | null
    gapSize?: number | null
  }): void {
    void surfaceVuexStore?.dispatch('post/sortedPost', payload)
  }

  function canShowDraggingOverlay(groupId): boolean {
    return isAPostDraggingAndNonmanualSorted.value && hoveredGroupIdWhenDraggingPost.value === groupId
  }

  const surfacePostSearch = useSurfacePostSearchStore()
  const { searchTerm } = storeToRefs(surfacePostSearch)
  const { filterPostsBySearchTerm } = surfacePostSearch
  const { filters } = storeToRefs(useSurfaceFilterStore())

  const postsFilteredBySearchAndFilters: Ref<Post[]> = ref(vuexCurrentSortedPosts.value)

  void unboundedWatchEffect(() => {
    if (!isAppUsing('surfaceSearchPosts')) return

    // Apply filters first
    const filteredPosts = filterPosts(vuexCurrentSortedPosts.value, filters.value)

    // Then apply search term
    if (searchTerm.value === '') {
      postsFilteredBySearchAndFilters.value = filteredPosts
      postMessage(postFilterResults(postsFilteredBySearchAndFilters.value))
    } else {
      void filterPostsBySearchTerm(filteredPosts, searchTerm.value).then((results) => {
        postsFilteredBySearchAndFilters.value = results
        postMessage(postFilterResults(postsFilteredBySearchAndFilters.value))
      })
    }
  })

  function shouldDisplayPost(postCid: Cid): boolean {
    return currentSortedPostsCids.value.includes(postCid)
  }

  return {
    // state
    isAPostDragging,
    hoveredGroupIdWhenDraggingPost,
    postCidBeingDragged,
    postBeingDragged,

    // getters
    canIDragPosts,
    allPostsCount,
    isAPostDraggingAndNonmanualSorted,
    publishedPostsCount,
    submittedPostsCount,
    sortedPostCids,
    allSortedPosts: vuexCurrentSortedPosts, // existing posts, sorted
    currentSortedPosts, // existing posts, sorted with search/filter applied.
    currentPostsCount,
    newestPostCid,
    postsBySectionId,
    sortedPostCidsBySectionId,
    postEntitiesByCid,
    postEntities,
    postCids,
    allSortedPostsByGroup, // existing group/posts map, sorted
    postsByGroup, // existing group/posts map, sorted with search/filter applied
    postsBySortedGroups,
    hasFetchedPosts,

    // actions
    shouldDisplayPost,
    canShowDraggingOverlay,
    startNewPost,
    sortPost,
  }
})
